// src/components/grupa_produse/GrupaProduseCreate.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import { components } from "react-select";
import AccountPickerModal from "../pickers/AccountPickerModal";
import VatPickerModal from "../pickers/VatPickerModal";
import { authFetch } from "../utils/authFetch"; // <- same-origin
// dacă vrei, poți scoate axios-ul complet din fișier

const LIMIT = 20;

const HighlightedOption = (props) => {
  const q = (props.selectProps.inputValue || "").trim();
  const label = props.data.label || "";
  const parts = q ? label.split(new RegExp(`(${q})`, "i")) : [label];
  return (
    <components.Option {...props}>
      {parts.map((p, i) =>
        q && p.toLowerCase() === q.toLowerCase() ? (
          <mark key={i}>{p}</mark>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </components.Option>
  );
};
function useLoader(resource, makeLabel) {
  return useCallback(
    async (inputValue, _loaded, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();
      const skip = (page - 1) * LIMIT;
      try {
        // IMPORTANT: trailing slash -> evită redirect-uri
        const data = await authFetch(
          `/${resource}/?search=${encodeURIComponent(q)}&skip=${skip}&limit=${LIMIT}`,
        );
        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.results)
              ? data.results
              : Array.isArray(data?.data)
                ? data.data
                : [];
        const total =
          typeof data?.total === "number"
            ? data.total
            : typeof data?.count === "number"
              ? data.count
              : typeof data?.total_count === "number"
                ? data.total_count
                : undefined;

        const options = items.map((it) => ({
          value: it.id,
          label:
            makeLabel?.(it) ??
            it.denumire ??
            it.name ??
            it.cod ??
            it.code ??
            it.descriere ??
            `#${it.id}`,
          __raw: it,
        }));

        const hasMore =
          typeof total === "number"
            ? skip + items.length < total
            : items.length === LIMIT;

        return { options, hasMore, additional: { page: page + 1 } };
      } catch (err) {
        console.error("loadOptions error", resource, err);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [resource, makeLabel],
  );
}

export default function GrupaProduseCreate() {
  const { id } = useParams();

  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    code: "",
    denumire: "",
    cota_tva_id: null,
    contintrare_id: null,
    contvanzare_id: null,
    contcheltuiala_id: null,
    tip_grupa: 0,
    cont_factura_debit_id: null,
  });

  // select state
  const [showVat, setShowVat] = useState(false);
  const [optContIntrare, setOptContIntrare] = useState(null);
  const [optContVanzare, setOptContVanzare] = useState(null);
  const [optContChelt, setOptContChelt] = useState(null);
  const [optContFacturaDebit, setOptContFacturaDebit] = useState(null);

  const [showContIntrare, setShowContIntrare] = useState(false);
  const [showContVanzare, setShowContVanzare] = useState(false);
  const [showContChelt, setShowContChelt] = useState(false);
  const [showContFacturaDebit, setShowContFacturaDebit] = useState(false);
  const [optTva, setOptTva] = useState(null);

  const makeVatLabel = useCallback((v) => {
    const cod = v.cod || v.code || "";
    const proc = v.procent ?? v.percent ?? "";
    const descr = v.descriere || v.description || "";
    return (
      [cod, proc !== "" ? `${proc}%` : "", descr].filter(Boolean).join(" — ") ||
      `TVA #${v.id}`
    );
  }, []);

  const makeAccountLabel = useCallback((c) => {
    const nr =
      c.simbol ??
      c.bxsimbol ??
      c.cont ??
      c.number ??
      c.numar ??
      c.account ??
      c.code ??
      c.cod ??
      "";
    const den = c.denumire ?? c.name ?? c.descriere ?? c.title ?? "";
    return [nr, den].filter(Boolean).join(" — ") || `Account #${c.id}`;
  }, []);

  const loadVatOptions = useLoader("bxvat", makeVatLabel);
  const loadAccountOptions = useLoader("accounts", makeAccountLabel);
  const additional = useMemo(() => ({ page: 1 }), []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await authFetch(`/grupa_produse/`);
        setForm({
          code: data.code ?? "",
          denumire: data.denumire ?? "",
          cota_tva_id: data.cota_tva_id ?? null,
          contintrare_id: data.contintrare_id ?? null,
          contvanzare_id: data.contvanzare_id ?? null,
          contcheltuiala_id: data.contcheltuiala_id ?? null,
          tip_grupa: data.tip_grupa ?? 0,
          cont_factura_debit_id: data.cont_factura_debit_id ?? null,
        });

        // pre-select options by id (fetch details for labels)
        const promises = [];

        if (data.cota_tva_id) {
          promises.push(
            authFetch(`/bxvat/}`)
              .then((v) => setOptTva(v))
              .catch(() =>
                setOptTva({
                  id: data.cota_tva_id,
                  cod: "",
                  descriere: `TVA #${data.cota_tva_id}`,
                }),
              ),
          );
        } else setOptTva(null);
        const preloadAccount = async (accId, setter, fallbackLabel) => {
          if (!accId) {
            setter(null);
            return;
          }
          try {
            const a = await authFetch(`/accounts/${accId}`);
            setter(a);
          } catch {
            setter({
              id: accId,
              simbol: "",
              denumire: fallbackLabel || `Account #${accId}`,
            });
          }
        };

        promises.push(
          preloadAccount(
            data.contintrare_id,
            setOptContIntrare,
            `Intrare #${data.contintrare_id}`,
          ),
        );
        promises.push(
          preloadAccount(
            data.contvanzare_id,
            setOptContVanzare,
            `Vânzare #${data.contvanzare_id}`,
          ),
        );
        promises.push(
          preloadAccount(
            data.contcheltuiala_id,
            setOptContChelt,
            `Cheltuială #${data.contcheltuiala_id}`,
          ),
        );

        promises.push(
          preloadAccount(
            data.cont_factura_debit_id,
            setOptContFacturaDebit,
            "Marfuri in curs de aprovizionare",
          ),
        );

        await Promise.all(promises);
      } catch (e) {
        setErr(String(e?.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, makeVatLabel, makeAccountLabel]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
            ? value === ""
              ? ""
              : Number(value)
            : value,
    }));
  };

  const handlePickVat = (vat) => {
    console.log("VAT PICKED:", vat);

    const raw = vat?.__raw ?? vat;
    const id = raw?.id ?? vat?.value ?? null;

    setOptTva(raw && typeof raw === "object" ? raw : null);
    setForm((f) => ({ ...f, cota_tva_id: id }));
    setShowVat(false);
  };

  const onChangeTva = (opt) => {
    setOptTva(opt);
    setForm((f) => ({ ...f, cota_tva_id: opt ? Number(opt.value) : null }));
  };
  const onChangeContIntrare = (opt) => {
    setOptContIntrare(opt);
    setForm((f) => ({ ...f, contintrare_id: opt ? Number(opt.value) : null }));
  };
  const onChangeContVanzare = (opt) => {
    setOptContVanzare(opt);
    setForm((f) => ({ ...f, contvanzare_id: opt ? Number(opt.value) : null }));
  };
  const onChangeContChelt = (opt) => {
    setOptContChelt(opt);
    setForm((f) => ({
      ...f,
      contcheltuiala_id: opt ? Number(opt.value) : null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const payload = {
        ...form,
        tip_grupa: Number(form.tip_grupa) || 0,
      };
      await authFetch(`/grupa_produse/`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      navigate("/grupa_produse");
    } catch (err) {
      setErr("Eroare la salvare: " + (err?.message ?? "unknown"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="mt-4">
      <Card className="p-3">
        <h3>Creare GrupaProduse</h3>
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>code</Form.Label>
                <Form.Control
                  type="text"
                  name="code"
                  value={form.code ?? ""}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>denumire</Form.Label>
                <Form.Control
                  type="text"
                  name="denumire"
                  value={form.denumire ?? ""}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>

            {/* TVA */}
            <Form.Group className="mb-3">
              <Form.Label>cota TVA</Form.Label>

              <div
                className="form-control d-flex align-items-center justify-content-between"
                style={{ cursor: "pointer" }}
                role="button"
                tabIndex={0}
                onClick={() => setShowVat(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowVat(true);
                  }
                }}
              >
                <span className={optTva ? "" : "text-muted"}>
                  {optTva ? makeVatLabel(optTva) : "Click pentru a alege TVA…"}
                </span>

                {optTva ? (
                  <span
                    title="Șterge selecția"
                    style={{ padding: "0 6px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOptTva(null);
                      setForm((f) => ({ ...f, cota_tva_id: null }));
                    }}
                  >
                    ✕
                  </span>
                ) : (
                  <span className="text-muted">▾</span>
                )}
              </div>
            </Form.Group>

            <span className={optContIntrare ? "" : "text-muted"}>
              {optContIntrare
                ? "Cont intrare"
                : "Click pentru a alege cont intrare…"}
            </span>
            {/* Conturi — accounts */}
            <div
              className="form-control d-flex align-items-center justify-content-between"
              style={{ cursor: "pointer" }}
              role="button"
              tabIndex={0}
              onClick={() => setShowContIntrare(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowContIntrare(true);
                }
              }}
            >
              <span className={optContIntrare ? "" : "text-muted"}>
                {optContIntrare
                  ? `${optContIntrare.code ?? optContIntrare.cod ?? ""} • ${
                      optContIntrare.denumire ?? optContIntrare.name ?? ""
                    }`
                  : "Click pentru a alege cont intrare…"}
              </span>

              {optContIntrare ? (
                <span
                  title="Șterge selecția"
                  style={{ padding: "0 6px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOptContIntrare(null);
                    setForm((f) => ({ ...f, contintrare_id: null }));
                  }}
                >
                  ✕
                </span>
              ) : (
                <span className="text-muted">▾</span>
              )}
            </div>

            <span className={optContVanzare ? "" : "text-muted"}>
              {optContIntrare
                ? "Cont vanzare"
                : "Click pentru a alege cont vanzare"}
            </span>

            <div
              className="form-control d-flex align-items-center justify-content-between"
              style={{ cursor: "pointer" }}
              role="button"
              tabIndex={0}
              onClick={() => setShowContVanzare(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowContVanzare(true);
                }
              }}
            >
              <span className={optContVanzare ? "" : "text-muted"}>
                {optContVanzare
                  ? `${optContVanzare.code ?? optContVanzare.cod ?? ""} • ${
                      optContVanzare.denumire ?? optContVanzare.name ?? ""
                    }`
                  : "Click pentru a alege cont Vanzare…"}
              </span>

              {optContVanzare ? (
                <span
                  title="Șterge selecția"
                  style={{ padding: "0 6px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOptContVanzare(null);
                    setForm((f) => ({ ...f, contvanzare_id: null }));
                  }}
                >
                  ✕
                </span>
              ) : (
                <span className="text-muted">▾</span>
              )}
            </div>
            <span className={optContChelt ? "" : "text-muted"}>
              {optContChelt
                ? "Cont cheltuiala"
                : "Click pentru a alege cont cheltuiala"}
            </span>
            <div
              className="form-control d-flex align-items-center justify-content-between"
              style={{ cursor: "pointer" }}
              role="button"
              tabIndex={0}
              onClick={() => setShowContChelt(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowContChelt(true);
                }
              }}
            >
              <span className={optContChelt ? "" : "text-muted"}>
                {optContChelt
                  ? `${optContChelt.code ?? optContChelt.cod ?? ""} • ${
                      optContChelt.denumire ?? optContChelt.name ?? ""
                    }`
                  : "Click pentru a alege cont Cheltuiala…"}
              </span>

              {optContChelt ? (
                <span
                  title="Șterge selecția"
                  style={{ padding: "0 6px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOptContChelt(null);
                    setForm((f) => ({ ...f, contcheltuiala_id: null }));
                  }}
                >
                  ✕
                </span>
              ) : (
                <span className="text-muted">▾</span>
              )}
            </div>
            {form.tip_grupa === 2 && (
              <>
                <span>Cont debit factură</span>
                <div
                  className="form-control d-flex align-items-center justify-content-between bg-light mb-3"
                  style={{ cursor: "pointer" }}
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowContFacturaDebit(true)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setShowContFacturaDebit(true); } }}
                >
                  <span>
                    {optContFacturaDebit
                      ? `${optContFacturaDebit.code ?? optContFacturaDebit.cod ?? ""} • ${
                          optContFacturaDebit.denumire ??
                          optContFacturaDebit.name ??
                          ""
                        }`
                      : "Alege contul de debit al facturii…"}
                  </span>
                </div>
              </>
            )}

            <Col md={6}>
              <Form.Group>
                <Form.Label>Tip grupă</Form.Label>

                <Form.Check
                  type="radio"
                  label="Servicii primite"
                  name="tip_grupa"
                  value="1"
                  checked={form.tip_grupa === 1}
                  onChange={() => setForm({ ...form, tip_grupa: 1 })}
                />

                <Form.Check
                  type="radio"
                  label="Stocabil"
                  name="tip_grupa"
                  value="2"
                  checked={form.tip_grupa === 2}
                  onChange={() => setForm({ ...form, tip_grupa: 2, cont_factura_debit_id: form.cont_factura_debit_id ?? null })}
                />

                <Form.Check
                  type="radio"
                  label="Servicii prestate"
                  name="tip_grupa"
                  value="3"
                  checked={form.tip_grupa === 3}
                  onChange={() => setForm({ ...form, tip_grupa: 3 })}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="text-end">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Se salvează…" : "Salvează"}
            </Button>
          </div>
        </Form>
      </Card>
      <AccountPickerModal
        show={showContIntrare}
        onHide={() => setShowContIntrare(false)}
        onSelect={(acc) => {
          setOptContIntrare(acc);
          setForm((f) => ({ ...f, contintrare_id: acc.id }));
        }}
      />

      <AccountPickerModal
        show={showContVanzare}
        onHide={() => setShowContVanzare(false)}
        onSelect={(acc) => {
          setOptContVanzare(acc);
          setForm((f) => ({ ...f, contvanzare_id: acc.id }));
        }}
      />

      <AccountPickerModal
        show={showContChelt}
        onHide={() => setShowContChelt(false)}
        onSelect={(acc) => {
          setOptContChelt(acc);
          setForm((f) => ({ ...f, contcheltuiala_id: acc.id }));
        }}
      />

      <AccountPickerModal
        show={showContFacturaDebit}
        onHide={() => setShowContFacturaDebit(false)}
        onSelect={(acc) => {
          setOptContFacturaDebit(acc);
          setForm((f) => ({ ...f, cont_factura_debit_id: acc.id }));
        }}
      />
      <VatPickerModal
        show={showVat}
        onHide={() => setShowVat(false)}
        onPick={(vat) => {
          console.log("PICK VAT:", vat);
          const raw = vat?.__raw ?? vat; // dacă vine option
          const id = raw?.id ?? vat?.value ?? null;

          setOptTva(raw); // ținem raw TVA
          setForm((f) => ({ ...f, cota_tva_id: id }));
        }}
      />
    </Container>
  );
}
